import type { ChartDescriptor } from '../charts/chartTypes.js'
import type { ChartSpec, ChartStyle, ChartType } from '../../../types/chart.js'

export function createAnalysisChartSpec(partial: {
  id: string
  title?: string
  type: ChartType
  style?: Partial<ChartStyle>
}): ChartSpec {
  return {
    version: 1,
    id: partial.id,
    workspaceId: 'analysis',
    title: partial.title || '',
    dataset: 'work',
    type: partial.type,
    measure: { field: 'count', agg: 'count' },
    range: { kind: 'all' },
    filters: [],
    style: {
      palette: 'theme',
      legend: 'off',
      dataLabels: false,
      gridlines: true,
      ...partial.style,
    },
    createdBy: 'system',
    createdAt: new Date().toISOString(),
    updatedBy: 'system',
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Mapping table from Analysis ChartDescriptor to custom ChartSpec (where supported).
 * Returns undefined when the chart does not map to a standard dataset/field.
 *
 * Mapped:
 * - work-composition / composition: dataset 'work', type 'donut', breakdown 'type', measure count
 * - signals-over-time / trends: dataset 'work', type 'area', timeField 'created', measure count
 * - work-status: dataset 'work', type 'bar', breakdown 'status', measure count
 * - work-assignee / assignee-bars: dataset 'work', type 'bar', breakdown 'assignee', measure count
 *
 * Unmapped (no Save to library button):
 * - score-ring / health: composite multi-factor score algorithm across 4 domains (execution, blocker, drift, ops), not a single collection/field
 * - sub-score-bars: composite algorithmic domain weights, not a single dataset field
 * - blocker-graph: heuristic dependency and blocker relationship traversal
 * - drift-scatter: statistical regression/drift analysis comparing multiple metric baselines
 * - readiness-matrix: cross-entity evaluation matrix
 */
export function mapAnalysisChartToSpec(descriptor: ChartDescriptor): ChartSpec | undefined {
  const baseStyle: ChartStyle = {
    palette: 'theme',
    legend: 'auto',
    dataLabels: false,
    gridlines: true,
  }
  const idPrefix = descriptor.id.startsWith('composition')
    ? 'composition'
    : descriptor.id.startsWith('multi-series')
      ? 'signals-over-time'
      : descriptor.id

  switch (idPrefix) {
    case 'work-composition':
    case 'composition':
      return {
        version: 1,
        id: `analysis-${descriptor.id}`,
        workspaceId: '',
        title: descriptor.title,
        dataset: 'work',
        type: 'donut',
        breakdown: { field: 'type' },
        measure: { field: 'count', agg: 'count' },
        range: { kind: 'all' },
        filters: [],
        style: baseStyle,
        createdBy: 'system',
        createdAt: new Date().toISOString(),
        updatedBy: 'system',
        updatedAt: new Date().toISOString(),
      }
    case 'signals-over-time':
    case 'trends':
      return {
        version: 1,
        id: `analysis-${descriptor.id}`,
        workspaceId: '',
        title: descriptor.title,
        dataset: 'work',
        type: 'area',
        time: { field: 'createdAt', bucket: 'week' },
        measure: { field: 'count', agg: 'count' },
        range: { kind: 'relative', last: 30, unit: 'day' },
        filters: [],
        style: baseStyle,
        createdBy: 'system',
        createdAt: new Date().toISOString(),
        updatedBy: 'system',
        updatedAt: new Date().toISOString(),
      }
    case 'work-status':
      return {
        version: 1,
        id: `analysis-${descriptor.id}`,
        workspaceId: '',
        title: descriptor.title,
        dataset: 'work',
        type: 'bar',
        breakdown: { field: 'status' },
        measure: { field: 'count', agg: 'count' },
        range: { kind: 'all' },
        filters: [],
        style: baseStyle,
        createdBy: 'system',
        createdAt: new Date().toISOString(),
        updatedBy: 'system',
        updatedAt: new Date().toISOString(),
      }
    case 'work-assignee':
    case 'assignee-bars':
      return {
        version: 1,
        id: `analysis-${descriptor.id}`,
        workspaceId: '',
        title: descriptor.title,
        dataset: 'work',
        type: 'bar',
        breakdown: { field: 'assignee' },
        measure: { field: 'count', agg: 'count' },
        range: { kind: 'all' },
        filters: [],
        style: baseStyle,
        createdBy: 'system',
        createdAt: new Date().toISOString(),
        updatedBy: 'system',
        updatedAt: new Date().toISOString(),
      }
    default:
      return undefined
  }
}
